// home-model.js -- the Home tab's derived view model (Home live-slip strip spec §4).
// Single job: turn App-owned `bets` + `games` into what Home renders -- mode (slips |
// empty), one status bucket per active slip, the exposure totals, the rail (my games
// first, LIVE-first) and the global live count. Pure: no React, no fetch, no storage, no
// Sentry. Reads bets/games; writes nothing. Does NOT compute status (betDisplayStatus /
// isSettlementPendingForBet own that) and does NOT render.
import { betDisplayStatus, calcPayout, isSettlementPendingForBet } from "./betting";

// #1353 PR 3b (r5b.4 I1): client grading trusts the server-written DNP flag.
const TRUST_DNP = { trustDnpFlag: true };

// Statuses that appear on the rail; POSTPONED/DELAYED/unknown are excluded and counted
// nowhere (Home Tab spec §6). Moved here from HomePage.jsx -- single owner.
export const RAIL_STATUSES = new Set(["LIVE", "UPCOMING", "FINAL", "OFF"]);
const RAIL_ORDER = { LIVE: 0, UPCOMING: 1, FINAL: 2, OFF: 2 };
const EMPTY_COUNTS = () => ({ live: 0, winning: 0, losing: 0, pending: 0, settling: 0 });

// SlipTicket's multi definition (spec §4): a legacy 'sgp+' record is a single here,
// exactly as the ticket renders it.
const isMultiBet = (bet) => bet.slipType === "parlay" || bet.slipType === "sgp";
const findGame = (games, id) => games.find((g) => String(g.id) === String(id));

// Exposure -- moved from HomePage.jsx (Home Tab spec §5 "do not re-derive"); the only change
// is the String() coercion on combinedOdds, mirroring SlipTicket.jsx:88 (pre-Codex r1).
function exposure(active) {
  const atRisk = active.reduce((s, b) => s + (Number(b.stake) || 0), 0);
  const toWin = active.reduce((s, b) => {
    const o = b.slipType === "single" ? b.odds : Number(String(b.combinedOdds || "+100").replace("+", ""));
    return s + Number(calcPayout(b.stake, o));
  }, 0);
  return { atRisk, toWin };
}

// One bucket per slip. Settling is checked FIRST so a slip the status machine computes as
// `winning` while every game is FINAL (hotspot 10a, the #1118 FTTS case) is SETTLING, never
// LIVE. #1655 B3b: a `not_graded` slip gets its own bucket, counted nowhere and kept out of
// exposure (D3). Anything else an unsettled bet can compute that is not a live sub-state or
// pending (won/lost before the cron, void/push, unresolvable, unknown) is PENDING -- fail closed.
function bucketFor(bet, games) {
  const isMulti = isMultiBet(bet);
  const b = isMulti ? { ...bet, legs: bet.legs || [] } : bet;
  const game = isMulti ? null : findGame(games, b.gameId);
  if (isSettlementPendingForBet({ bet: b, game, games, isMulti })) return "settling";
  switch (betDisplayStatus(b, games, { isMulti, ...TRUST_DNP })) {
    case "winning": return "winning";
    case "losing": return "losing";
    case "close": return "close";
    case "not_graded": return "not_graded";
    default: return "pending";
  }
}

// String(gameId) -> number of my legs on it. Map only -- never an object keyed by an id
// (the #1116 prototype-key class). Empty / null ids are skipped, never matched.
function myGames(active) {
  const map = new Map();
  const add = (id) => {
    if (id == null || id === "") return;
    const key = String(id);
    map.set(key, (map.get(key) || 0) + 1);
  };
  for (const bet of active) {
    if (isMultiBet(bet)) {
      const isSGP = bet.slipType === "sgp";
      for (const leg of bet.legs || []) add(leg?.gameId || (isSGP ? bet.gameId : ""));
    } else {
      add(bet.gameId);
    }
  }
  return map;
}

// Mine first, then everyone else; each group LIVE -> UPCOMING -> FINAL/OFF. Array.prototype
// .sort is stable (ES2019), so today's within-status order is preserved.
function buildRail(games, mine) {
  const byStatus = (a, b) => RAIL_ORDER[a.game.status] - RAIL_ORDER[b.game.status];
  const entries = games
    .filter((g) => RAIL_STATUSES.has(g.status))
    .map((game) => ({ game, legCount: mine.get(String(game.id)) || 0 }));
  const own = entries.filter((e) => e.legCount > 0).sort(byStatus);
  const rest = entries.filter((e) => e.legCount === 0).sort(byStatus);
  return [...own, ...rest];
}

// `games` is the realtime window (the rail and liveCount). `statusGames` (#1655 B3b, D-P2-1) is
// the window plus the not-graded fetch's old final rows; it decides buckets and exposure only,
// so old finals never reach the rail. It defaults to `games`.
export function buildHomeModel({ bets, games, statusGames } = {}) {
  const betList = Array.isArray(bets) ? bets : [];
  // null entries are dropped here so every consumer below can read `g.id` / `g.status` (Ultron F9).
  const gameList = (Array.isArray(games) ? games : []).filter(Boolean);
  const statusList = Array.isArray(statusGames) ? statusGames.filter(Boolean) : gameList;
  const active = betList.filter((b) => b && !b.settledAt);
  const counts = EMPTY_COUNTS();
  const exposed = [];
  for (const bet of active) {
    const bucket = bucketFor(bet, statusList);
    if (bucket !== "not_graded") exposed.push(bet);
    if (bucket === "winning" || bucket === "losing" || bucket === "close") counts.live += 1;
    if (bucket === "winning") counts.winning += 1;
    else if (bucket === "losing") counts.losing += 1;
    else if (bucket === "settling") counts.settling += 1;
    else if (bucket === "pending") counts.pending += 1;
  }
  const { atRisk, toWin } = exposure(exposed);
  return {
    mode: active.length > 0 ? "slips" : "empty",
    counts,
    atRisk, // reserved -- spec §4 mandates it; Home renders TO WIN only (strip §5)
    toWin,
    rail: buildRail(gameList, myGames(active)),
    liveCount: gameList.filter((g) => g.status === "LIVE").length,
  };
}
